import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { 
  baseSEO, 
  generateMetaTags, 
  generateFAQSchema, 
  generateBreadcrumbSchema,
  richSnippetsData 
} from '../../lib/seoConfig';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  article?: boolean;
  noindex?: boolean;
  structuredData?: any[];
  breadcrumbs?: { name: string; url: string }[];
}

export const SEO: React.FC<SEOProps> = ({
  title: customTitle,
  description: customDescription,
  keywords: customKeywords,
  image: customImage,
  article = false,
  noindex = false,
  structuredData: customStructuredData,
  breadcrumbs
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Get page-specific SEO data
  const pageSEO = generateMetaTags(pathname);
  
  // Use custom values if provided, otherwise use page defaults
  const title = customTitle || pageSEO.title;
  const description = customDescription || pageSEO.description;
  const keywords = customKeywords || pageSEO.keywords;
  const image = customImage || pageSEO.ogImage;
  const canonical = pageSEO.canonical;
  
  // Generate default breadcrumbs if not provided
  const defaultBreadcrumbs = [
    { name: 'Home', url: baseSEO.siteUrl },
    ...(pathname !== '/' ? [{ name: title.split(' - ')[0], url: `${baseSEO.siteUrl}${pathname}` }] : [])
  ];
  
  const finalBreadcrumbs = breadcrumbs || defaultBreadcrumbs;
  
  // Combine all structured data
  const allStructuredData = [
    ...(pageSEO.structuredData || []),
    ...(customStructuredData || []),
    generateBreadcrumbSchema(finalBreadcrumbs),
    richSnippetsData.organization
  ];
  
  // Add FAQ schema to homepage
  if (pathname === '/') {
    allStructuredData.push(generateFAQSchema());
  }
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={baseSEO.siteName} />
      <meta property="og:locale" content={baseSEO.locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={baseSEO.twitterHandle} />
      <meta name="twitter:creator" content={baseSEO.twitterHandle} />
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content={baseSEO.themeColor} />
      <meta name="apple-mobile-web-app-title" content="MockTrail" />
      <meta name="application-name" content="MockTrail" />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large" />}
      
      {/* Language alternates */}
      <link rel="alternate" hreflang="en" href={canonical} />
      <link rel="alternate" hreflang="en-IN" href={canonical} />
      <link rel="alternate" hreflang="x-default" href={canonical} />
      
      {/* Additional SEO meta tags from page config */}
      {pageSEO.additionalMetaTags?.map((tag, index) => (
        <meta key={index} name={tag.name} content={tag.content} />
      ))}
      
      {/* Structured Data */}
      {allStructuredData.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
      
      {/* Preload critical resources */}
      <link rel="preload" as="image" href="/mocktrail-logo-bg.png" />
      <link rel="preload" as="image" href="/optimized/mocktrail-hero-lightui.webp" />
      
      {/* Prefetch important pages */}
      <link rel="prefetch" href="/pricing" />
      <link rel="prefetch" href="https://platform.mocktrail.app" />
    </Helmet>
  );
};